import * as React from 'react';
import { RefreshCw, Save, DollarSign, TrendingUp, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { getCreditPricing, updateCreditPricing, refreshCreditPricing, type CreditPricingItem } from '@/api/client';

export function CreditPricingTab() {
  const [items, setItems] = React.useState<CreditPricingItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [saving, setSaving] = React.useState<Record<string, boolean>>({});
  const [editValues, setEditValues] = React.useState<Record<string, string>>({});
  const [costValues, setCostValues] = React.useState<Record<string, string>>({});
  const [refreshResult, setRefreshResult] = React.useState<string | null>(null);
  const [creditCnyPerPoint, setCreditCnyPerPoint] = React.useState(0.1);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCreditPricing();
      setItems(data.items || []);
      setCreditCnyPerPoint(data.credit_cny_per_point);
      // 初始化编辑值
      const ev: Record<string, string> = {};
      const cv: Record<string, string> = {};
      for (const item of data.items || []) {
        ev[item.operation_key] = String(item.credit_cost);
        cv[item.operation_key] = String(item.cost_value);
      }
      setEditValues(ev);
      setCostValues(cv);
    } catch (e) {
      console.error('加载积分定价失败', e);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshResult(null);
    try {
      const result = await refreshCreditPricing();
      const updates = result.updates || [];
      if (updates.length > 0) {
        setRefreshResult(`已更新 ${updates.length} 项成本，刷新时间 ${result.fetched_at}`);
      } else {
        setRefreshResult('刷新完成，无成本更新');
      }
      await load();
    } catch (e: any) {
      setRefreshResult(`刷新失败: ${e.message || '未知错误'}`);
    } finally {
      setRefreshing(false);
    }
  };

  const handleSave = async (item: CreditPricingItem) => {
    const opKey = item.operation_key;
    setSaving(prev => ({ ...prev, [opKey]: true }));
    try {
      const creditCost = parseFloat(editValues[opKey] || '0');
      const costValue = parseFloat(costValues[opKey] || '0');
      await updateCreditPricing(opKey, {
        credit_cost: isNaN(creditCost) ? item.credit_cost : creditCost,
        cost_value: isNaN(costValue) ? undefined : costValue,
      });
      await load();
    } catch (e) {
      console.error('保存失败', e);
    } finally {
      setSaving(prev => ({ ...prev, [opKey]: false }));
    }
  };

  const getMarginColor = (pct: number) => {
    if (pct >= 80) return 'text-green-600';
    if (pct >= 50) return 'text-emerald-600';
    if (pct >= 30) return 'text-amber-600';
    return 'text-red-600';
  };

  const getMarginBg = (pct: number) => {
    if (pct >= 80) return 'bg-green-50 border-green-200';
    if (pct >= 50) return 'bg-emerald-50 border-emerald-200';
    if (pct >= 30) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              积分定价管理
            </CardTitle>
            <CardDescription>
              设置每项操作的消耗积分，系统自动计算售价和利润率。1 积分 = ¥{creditCnyPerPoint}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`mr-1 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? '刷新中...' : '刷新供应商价格'}
            </Button>
          </div>
        </div>
        {refreshResult && (
          <div className="mt-2 flex items-center gap-2 rounded-md border bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {refreshResult}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">加载中...</div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">暂无定价配置</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs font-medium uppercase text-muted-foreground">
                  <th className="pb-2 pr-4">操作</th>
                  <th className="pb-2 pr-4">平台</th>
                  <th className="pb-2 pr-4">单位</th>
                  <th className="pb-2 pr-4">成本来源</th>
                  <th className="pb-2 pr-4 text-right">单位成本 (¥)</th>
                  <th className="pb-2 pr-4 text-right">消耗积分</th>
                  <th className="pb-2 pr-4 text-right">售价 (¥)</th>
                  <th className="pb-2 pr-4 text-right">利润率</th>
                  <th className="pb-2 pr-2 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const priceCny = (parseFloat(editValues[item.operation_key] || '0') || 0) * creditCnyPerPoint;
                  const cost = parseFloat(costValues[item.operation_key] || '0') || 0;
                  const margin = priceCny > 0 ? ((priceCny - cost) / priceCny * 100) : 0;
                  return (
                    <tr key={item.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-2.5 pr-4 font-medium">{item.display_name}</td>
                      <td className="py-2.5 pr-4">
                        <Badge variant="outline" className="text-xs">
                          {item.platform === 'wechat' ? '视频号' : item.platform === 'other' ? '抖音/小红书' : '通用'}
                        </Badge>
                      </td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{item.unit}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{item.cost_source}</td>
                      <td className="py-2.5 pr-4 text-right">
                        <Input
                          className="ml-auto h-8 w-20 text-right"
                          value={costValues[item.operation_key] ?? ''}
                          onChange={(e) => setCostValues(prev => ({ ...prev, [item.operation_key]: e.target.value }))}
                          step="0.0001"
                          min="0"
                          type="number"
                        />
                      </td>
                      <td className="py-2.5 pr-4 text-right">
                        <Input
                          className="ml-auto h-8 w-20 text-right"
                          value={editValues[item.operation_key] ?? ''}
                          onChange={(e) => setEditValues(prev => ({ ...prev, [item.operation_key]: e.target.value }))}
                          step="0.1"
                          min="0"
                          type="number"
                        />
                      </td>
                      <td className="py-2.5 pr-4 text-right font-mono tabular-nums">
                        ¥{priceCny.toFixed(3)}
                      </td>
                      <td className="py-2.5 pr-4 text-right">
                        <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono text-xs font-semibold tabular-nums ${getMarginBg(margin)} ${getMarginColor(margin)}`}>
                          <TrendingUp className="h-3 w-3" />
                          {margin.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-2.5 pr-2 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8"
                          onClick={() => handleSave(item)}
                          disabled={saving[item.operation_key]}
                        >
                          <Save className="mr-1 h-3.5 w-3.5" />
                          {saving[item.operation_key] ? '保存中' : '保存'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-4 rounded-md border bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
          <p className="font-medium">说明：</p>
          <ul className="mt-1 list-inside list-disc space-y-0.5">
            <li>利润率 = (售价 − 成本) / 售价。售价 = 消耗积分 × ¥{creditCnyPerPoint}</li>
            <li>点击「刷新供应商价格」从 DeepSeek / TikHub 官方获取最新成本</li>
            <li>消耗积分支持小数（如 1.5、2.5），成本支持四位小数</li>
            <li>修改后点击「保存」立即生效，积分扣减逻辑使用最新配置</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}